import { formatName } from "../../../../../utils/format-name.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PackageOpen, RotateCcw, TriangleAlert } from "lucide-react";

import {
  ASSIGN_ASSET,
  DEALLOCATE_ASSET,
  DEALLOCATED_ASSETS,
} from "../../../../../../config/Endpoints.js";
import { ApiError, getApi, postApi } from "../../../../../utils/api.ts";
import { showError } from "../../../../../utils/toast.ts";
import { useSqeLabs } from "../../../../../hooks/use-sqe-labs.ts";
import { accessToken } from "../../../../../utils/auth.ts";
import {
  Button,
  Modal,
  Select,
  Spinner,
  TextArea,
  TextField,
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { Card } from "../../../../shared/card/card.tsx";
import styles from "./allocated-assets-table.module.css";

type AssignedAsset = {
  assignedAssestId: number;
  manufacture: string | null;
  serialNumber: string | null;
  owner: string | null;
  comments: string | null;
  assestName: string | null;
  empId: number;
  empName: string | null;
  allocatedDate: string | null;
  purchaseDate: string | null;
  model: string | null;
  warrantyExpired: string | null;
};

type AssignedAssetRow = AssignedAsset & Record<string, unknown>;

type AssetResponse = {
  data?: AssignedAsset[];
};

type DeallocateForm = {
  assetCondition: string;
  repairCost: string;
  comment: string;
};

type DeallocatedAsset = {
  assignedAssetId: number;
  assetId: number;
  assetName: string | null;
  employeeId: number;
  employeeName: string | null;
  assetCondition: string | null;
  status: string | null;
  repairCost: number | null;
  deallocatedComments: string | null;
  serialNumber: string | null;
  assetAvailability: string | null;
  assetStatus: string | null;
  updatedAt: string | null;
};

type DeallocatedAssetRow = DeallocatedAsset & {
  assetConditionLabel: string;
  deallocatedCommentsLabel: string;
} & Record<string, unknown>;

interface AllocatedAssetsTableProps {
  employeeId: number;
  employeeName?: string;
  onAssetsCountChange?: (count: number) => void;
}

const ASSET_CONDITION_OPTIONS = [
  { value: "Partially damaged but working", label: "Partially damaged but working" },
  { value: "Only dent on body", label: "Only dent on body" },
  { value: "Fully damaged", label: "Fully damaged" },
  { value: "Good condition", label: "Good condition" },
];

const INITIAL_FORM: DeallocateForm = {
  assetCondition: "",
  repairCost: "",
  comment: "",
};

function displayValue(value: string | number | null | undefined): string {
  if (value == null || String(value).trim() === "") return "-";
  return String(value);
}

function formatDateTime(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function AllocatedAssetsTable({
  employeeId,
  employeeName,
  onAssetsCountChange,
}: AllocatedAssetsTableProps) {
  const isSqe = useSqeLabs();
  const [assets, setAssets] = useState<AssignedAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssignedAsset | null>(null);
  const [form, setForm] = useState<DeallocateForm>(INITIAL_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const selectedAssetName = useMemo(
    () =>
      selectedAsset == null
        ? ""
        : `${displayValue(selectedAsset.assestName)} - ${displayValue(selectedAsset.serialNumber)}`,
    [selectedAsset],
  );
  const assetRows = useMemo<AssignedAssetRow[]>(
    () => assets.map((asset) => ({ ...asset })),
    [assets],
  );
  const columns = useMemo<ResponsiveColumn<AssignedAssetRow>[]>(
    () => [
      {
        id: "manufacture",
        header: "Manufacturer",
        accessor: "manufacture",
        sortable: true,
        truncate: true,
        render: (asset) => displayValue(asset.manufacture),
        getTitleText: (asset) => displayValue(asset.manufacture),
      },
      {
        id: "model",
        header: "Model",
        accessor: "model",
        sortable: true,
        truncate: true,
        render: (asset) => displayValue(asset.model),
        getTitleText: (asset) => displayValue(asset.model),
      },
      {
        id: "assestName",
        header: "Asset Type",
        accessor: "assestName",
        sortable: true,
        truncate: true,
        render: (asset) => displayValue(asset.assestName),
        getTitleText: (asset) => displayValue(asset.assestName),
      },
      {
        id: "serialNumber",
        header: "Serial Number",
        accessor: "serialNumber",
        sortable: true,
        nowrap: true,
        truncate: true,
        render: (asset) => displayValue(asset.serialNumber),
        getTitleText: (asset) => displayValue(asset.serialNumber),
      },
      {
        id: "owner",
        header: "Owner",
        accessor: "owner",
        sortable: true,
        truncate: true,
        render: (asset) => displayValue(asset.owner),
        getTitleText: (asset) => displayValue(asset.owner),
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (asset) => (
          <Button
            variant="muted-outline"
            size="sm"
            startIcon={<RotateCcw size={14} aria-hidden />}
            onClick={() => setSelectedAsset(asset)}
          >
            Deallocate
          </Button>
        ),
      },
    ],
    [],
  );

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await getApi<AssetResponse>(
        `${ASSIGN_ASSET}?employeeId=${employeeId}`,
        accessToken(),
      );
      const nextAssets = Array.isArray(data?.data) ? data.data : [];
      setAssets(nextAssets);
      onAssetsCountChange?.(nextAssets.length);
    } catch (e) {
      const message =
        e instanceof ApiError ? e.message : "Unable to load allocated assets.";
      setError(message);
      if (isSqe) showError(message);
      setAssets([]);
      onAssetsCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [employeeId, onAssetsCountChange, isSqe]);

  useEffect(() => {
    void fetchAssets();
  }, [fetchAssets]);

  const closeModal = () => {
    if (submitting) return;
    setSelectedAsset(null);
    setForm(INITIAL_FORM);
    setFormError(null);
  };

  const handleSubmit = async () => {
    if (selectedAsset == null) return;

    if (!form.assetCondition || !form.repairCost || !form.comment.trim()) {
      setFormError("All fields are required.");
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      await postApi(
        DEALLOCATE_ASSET,
        accessToken(),
        {
          assestCondition: form.assetCondition,
          assignedAssestId: selectedAsset.assignedAssestId,
          repairCost: form.repairCost,
          comment: form.comment.trim(),
        },
      );
      setSelectedAsset(null);
      setForm(INITIAL_FORM);
      await fetchAssets();
    } catch (e) {
      const message =
        e instanceof ApiError ? e.message : "Unable to deallocate asset.";
      setFormError(message);
      if (isSqe) showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className="heading-sm">Allocated Assets</h3>
          <p className="body-sm">
            Assets currently assigned to {employeeName ? formatName(employeeName) : "this employee"}.
          </p>
        </div>
      </div>

      {loading ? <Spinner /> : null}

      {!loading && error ? (
        <div className={styles.errorState} role="alert">
          <TriangleAlert size={18} aria-hidden />
          <span className="body-sm">{error}</span>
        </div>
      ) : null}

      {!loading && !error && assets.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <PackageOpen size={18} aria-hidden />
          <span className="body-sm">No allocated assets found.</span>
        </div>
      ) : null}

      {!loading && !error && assets.length > 0 ? (
        <ResponsiveDataTable<AssignedAssetRow>
          serialNumber
          columns={columns}
          data={assetRows}
          defaultSortKey="assestName"
          defaultSortDir="asc"
          aria-label="Allocated assets"
          pagination={false}
        />
      ) : null}

      <Modal
        open={selectedAsset != null}
        onClose={closeModal}
        title="Asset Deallocation Action"
        size="md"
        footer={
          <>
           <div className="d-flex justify-content-end gap-1">
             <Button variant="light-grey" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </Button>
           </div>
          </>
        }
      >
        <div className={styles.form}>
          <TextField
            id="selected-asset"
            label="Asset"
            value={selectedAssetName}
            readOnly
          />
          <Select
            id="asset-condition"
            label="Asset Condition"
            required
            value={form.assetCondition}
            options={ASSET_CONDITION_OPTIONS}
            placeholder="Select asset condition"
            disabled={submitting}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, assetCondition: value }))
            }
          />
          <TextField
            id="repair-cost"
            label="Repair Cost"
            required
            inputMode="numeric"
            pattern="[0-9]*"
            value={form.repairCost}
            disabled={submitting}
            onChange={(event) => {
              const next = event.target.value.replace(/\D/g, "").slice(0, 6);
              setForm((prev) => ({ ...prev, repairCost: next }));
            }}
          />
          <TextArea
            id="asset-comment"
            label="Comment"
            required
            rows={3}
            value={form.comment}
            disabled={submitting}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, comment: event.target.value }))
            }
          />
          {formError ? (
            <div className={styles.errorState} role="alert">
              <TriangleAlert size={18} aria-hidden />
              <span className="body-sm">{formError}</span>
            </div>
          ) : null}
        </div>
      </Modal>
    </Card>
  );
}

export function DeallocatedAssetsTable({
  employeeId,
  employeeName,
}: AllocatedAssetsTableProps) {
  const isSqe = useSqeLabs();
  const [assets, setAssets] = useState<DeallocatedAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assetRows = useMemo<DeallocatedAssetRow[]>(
    () =>
      assets.map((asset) => ({
        ...asset,
        assetConditionLabel: displayValue(asset.assetCondition),
        deallocatedCommentsLabel: displayValue(asset.deallocatedComments),
      })),
    [assets],
  );

  const columns = useMemo<ResponsiveColumn<DeallocatedAssetRow>[]>(
    () => [
      {
        id: "assetName",
        header: "Asset",
        accessor: "assetName",
        sortable: true,
        truncate: true,
        render: (asset) => displayValue(asset.assetName),
        getTitleText: (asset) => displayValue(asset.assetName),
      },
      {
        id: "serialNumber",
        header: "Serial Number",
        accessor: "serialNumber",
        sortable: true,
        nowrap: true,
        truncate: true,
        render: (asset) => displayValue(asset.serialNumber),
        getTitleText: (asset) => displayValue(asset.serialNumber),
      },
      {
        id: "assetCondition",
        header: "Condition",
        accessor: "assetConditionLabel",
        sortable: true,
        truncate: true,
        getTitleText: (asset) => asset.assetConditionLabel,
      },
      {
        id: "repairCost",
        header: "Repair Cost",
        accessor: "repairCost",
        sortable: true,
        nowrap: true,
        render: (asset) => displayValue(asset.repairCost),
        getSortValue: (asset) => Number(asset.repairCost ?? 0),
      },
    
      {
        id: "assetStatus",
        header: "Asset Status",
        accessor: "assetStatus",
        sortable: true,
        nowrap: true,
        render: (asset) => displayValue(asset.assetStatus),
        getTitleText: (asset) => displayValue(asset.assetStatus),
      },
    
    
    ],
    [],
  );

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await getApi<DeallocatedAsset[]>(
        `${DEALLOCATED_ASSETS}/${employeeId}`,
        accessToken(),
      );
      setAssets(Array.isArray(data) ? data : []);
    } catch (e) {
      const message =
        e instanceof ApiError ? e.message : "Unable to load deallocated assets.";
      setError(message);
      if (isSqe) showError(message);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, isSqe]);

  useEffect(() => {
    void fetchAssets();
  }, [fetchAssets]);

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h3 className="heading-sm">Assets</h3>
          <p className="body-sm">
            Deallocated assets for {employeeName ? formatName(employeeName) : "this employee"}.
          </p>
        </div>
      </div>

      {loading ? <Spinner /> : null}

      {!loading && error ? (
        <div className={styles.errorState} role="alert">
          <TriangleAlert size={18} aria-hidden />
          <span className="body-sm">{error}</span>
        </div>
      ) : null}

      {!loading && !error && assets.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <PackageOpen size={18} aria-hidden />
          <span className="body-sm">No deallocated assets found.</span>
        </div>
      ) : null}

      {!loading && !error && assets.length > 0 ? (
        <ResponsiveDataTable<DeallocatedAssetRow>
          serialNumber
          columns={columns}
          data={assetRows}
          defaultSortKey="updatedAt"
          defaultSortDir="desc"
          aria-label="Deallocated assets"
          pagination={false}
        />
      ) : null}
    </Card>
  );
}
